/**
 * Geocoding utilities for converting place_id to coordinates
 */

/**
 * Method 1: Using react-google-places-autocomplete
 * @param {string} placeId - Google place ID
 * @returns {Promise<Object>} Coordinates in MongoDB GeoJSON format
 */
export const geocodeByPlaceIdMethod1 = async (placeId) => {
  try {
    const { geocodeByPlaceId } = await import('react-google-places-autocomplete')
    const results = await geocodeByPlaceId(placeId)

    if (results && results.length > 0) {
      const location = results[0].geometry.location
      return {
        type: 'Point',
        coordinates: [location.lng(), location.lat()] // [lng, lat] for MongoDB
      }
    }

    throw new Error('No results found')
  } catch (error) {
    console.error('Method 1 failed:', error)
    throw error
  }
}

/**
 * Method 2: Using Google Places Details API directly
 * @param {string} placeId - Google place ID
 * @returns {Promise<Object>} Coordinates in MongoDB GeoJSON format
 */
export const geocodeByPlaceIdMethod2 = async (placeId) => {
  const apiKey = process.env.REACT_APP_MAPS_API_KEY

  if (!apiKey) {
    throw new Error('Google Maps API key not found')
  }

  try {
    const response = await fetch(
      `https://maps.googleapis.com/maps/api/place/details/json?place_id=${placeId}&fields=geometry&key=${apiKey}`
    )

    const data = await response.json()

    if (data.status === 'OK' && data.result?.geometry?.location) {
      const { lat, lng } = data.result.geometry.location
      return {
        type: 'Point',
        coordinates: [lng, lat] // [lng, lat] for MongoDB
      }
    }

    throw new Error(`API Error: ${data.status}`)
  } catch (error) {
    console.error('Method 2 failed:', error)
    throw error
  }
}

/**
 * Method 3: Using Google Geocoding API
 * @param {string} placeId - Google place ID
 * @returns {Promise<Object>} Coordinates in MongoDB GeoJSON format
 */
export const geocodeByPlaceIdMethod3 = async (placeId) => {
  const apiKey = process.env.REACT_APP_MAPS_API_KEY

  if (!apiKey) {
    throw new Error('Google Maps API key not found')
  }

  try {
    const response = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?place_id=${placeId}&key=${apiKey}`)

    const data = await response.json()

    if (data.status === 'OK' && data.results?.length > 0) {
      const { lat, lng } = data.results[0].geometry.location
      return {
        type: 'Point',
        coordinates: [lng, lat] // [lng, lat] for MongoDB
      }
    }

    throw new Error(`Geocoding API Error: ${data.status}`)
  } catch (error) {
    console.error('Method 3 failed:', error)
    throw error
  }
}

/**
 * Robust geocoding function that tries multiple methods
 * @param {string} placeId - Google place ID
 * @returns {Promise<Object>} Coordinates in MongoDB GeoJSON format
 */
export const getCoordinatesFromPlaceId = async (placeId) => {
  const methods = [geocodeByPlaceIdMethod1, geocodeByPlaceIdMethod2, geocodeByPlaceIdMethod3]

  for (let i = 0; i < methods.length; i++) {
    try {
      const coordinates = await methods[i](placeId)
      return coordinates
    } catch (error) {
      console.warn(`❌ Method ${i + 1} failed:`, error.message)
      if (i === methods.length - 1) {
        throw new Error('All geocoding methods failed')
      }
    }
  }
}

/**
 * Helper function to validate coordinates
 * @param {Object} coordinates - Coordinates object
 * @returns {boolean} True if valid
 */
export const validateCoordinates = (coordinates) => {
  if (!coordinates || coordinates.type !== 'Point') {
    return false
  }

  const [lng, lat] = coordinates.coordinates || []

  return typeof lng === 'number' && typeof lat === 'number' && lng >= -180 && lng <= 180 && lat >= -90 && lat <= 90
}

/**
 * Simple usage function for clinic forms
 * @param {string} placeId - Google place ID
 * @param {string} address - Human readable address
 * @returns {Promise<Object>} Complete location data for saving
 */
export const getLocationDataForClinic = async (placeId, address) => {
  try {
    const coordinates = await getCoordinatesFromPlaceId(placeId)

    return {
      address,
      place_id: placeId,
      geolocation: coordinates,
      coordinatesFound: true,
      timestamp: new Date().toISOString()
    }
  } catch (error) {
    console.error('Failed to get coordinates:', error)

    // Return partial data without coordinates
    return {
      address,
      place_id: placeId,
      geolocation: null,
      coordinatesFound: false,
      error: error.message,
      timestamp: new Date().toISOString()
    }
  }
}
